import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  contentChild,
  effect,
  ElementRef,
  HostBinding,
  inject,
  Input,
  input,
  OnInit,
  output,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

import { CommonMediaQueries, MediaScreenService, SortComponent, SortDirective } from '@hglobal/common';
import { FormBuilder, FormGroup } from '@ngneat/reactive-forms';
import { GroupResolverFormBuilder } from '@ngneat/reactive-forms/lib/form-builder';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';

import { BaseFilters, Column, GetPaginationRes, PaginationEvent } from '@pp/core/entities';

import { PaginationComponent } from '../pagination/pagination.component';

@Component({
  selector: 'pp-table',
  imports: [SortComponent, SortDirective, ReactiveFormsModule, TranslateModule, AsyncPipe, NgTemplateOutlet, PaginationComponent],
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableComponent implements OnInit {
  readonly #fb = inject(FormBuilder);
  readonly #mediaScreenService = inject(MediaScreenService);

  readonly pageEvent = output<PaginationEvent>();
  readonly sortEvent = output<string>();
  readonly tableScroll = output<void>();
  readonly columns = input<Column[]>();
  readonly pageSizeOptions = input<number[]>([10, 20, 50]);
  readonly withPagination = input<boolean>(true);
  @HostBinding('style.gridTemplateColumns') @Input() gridTemplateColumns: string;
  readonly tableBody = viewChild<ElementRef<HTMLDivElement>>('tableBody');
  readonly templateMobile = contentChild<TemplateRef<unknown>>('mobile');
  readonly templateRef = contentChild(TemplateRef);
  form: FormGroup<GroupResolverFormBuilder<{ sort: string }>>;
  breakpoints$ = combineLatest({
    isDesktop: this.#mediaScreenService.mediaMatcher(CommonMediaQueries.LG),
  });
  pageIndex: number;
  pageSize: number;
  sort: string;

  readonly dataSourceEffect = effect(() => {
    const dataSource = this.dataSource();
    if (dataSource?.items.length === 0 && dataSource?.itemsCount > 0) {
      this.onPageChange({
        pageIndex: this.getLastPageIndex(dataSource.itemsCount),
        pageSize: this.pageSize,
      });
    }
  });

  dataSource = input<GetPaginationRes<unknown>>();

  @Input() set filters({ page, limit, sort }: BaseFilters) {
    this.pageIndex = page;
    this.pageSize = limit;
    this.sort = sort;
  }

  ngOnInit(): void {
    this.form = this.#fb.group({
      sort: this.sort,
    });

    this.form.controls.sort.valueChanges.subscribe((value: string) => {
      this.sortEvent.emit(value);
    });
  }

  onPageChange(event: PaginationEvent) {
    this.pageEvent.emit(event);
  }

  getLastPageIndex(itemsCount: number): number {
    return Math.ceil(itemsCount / this.pageSize);
  }
}
